import api from './axios';

export type OneMinutePaperEvidenceMeta = {
  index: number;
  fileName: string;
  mimeType: string;
};

export type OneMinutePaperReservaRow = {
  alumnoId: number;
  alumno: string;
  hasEntry: boolean;
  oneMinutePaperId: number | null;
  nota: number | null;
  registradoEn: string | null;
  evidencias: OneMinutePaperEvidenceMeta[];
};

export type OneMinutePaperReservaState = {
  reservaId: number;
  applicable: boolean;
  canUpload: boolean;
  deadlineAt: string | null;
  reason: string | null;
  rows: OneMinutePaperReservaRow[];
};

export type OneMinutePaperReportRow = {
  reservaId: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  asignatura: string;
  profesorId: number;
  profesor: string;
  oneMinutePaperId: number | null;
  nota: number | null;
  hasExercise: boolean;
  estado: 'registrado' | 'sin_ejercicio';
  registradoEn: string | null;
  evidenceCount: number;
  evidencias: OneMinutePaperEvidenceMeta[];
};

export type OneMinutePaperReportResponse = {
  alumnoId: number;
  desde: string;
  hasta: string;
  profesorId: number | null;
  total: number;
  rows: OneMinutePaperReportRow[];
};

export type OneMinutePaperComplianceProfesorRow = {
  profesorId: number;
  profesor: string;
  totalReservasAplicables: number;
  reservasSinEvaluacion: number;
  porcentajeIncumplimiento: number;
  enAlerta: boolean;
  ultimaPendiente: string | null;
};

export type OneMinutePaperComplianceResponse = {
  rows: OneMinutePaperComplianceProfesorRow[];
  totalProfesores: number;
  totalProfesoresEnAlerta: number;
  reservasSinEvaluacion: number;
  umbralAlerta: number;
  fechaInicioControl: string;
  desdeAplicado: string;
  hasta: string | null;
};

export type OneMinutePaperComplianceReservaRow = {
  reservaId: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  asignatura: string;
  centro: string;
  alumnosEsperados: number;
  evaluacionesRegistradas: number;
  alumnosPendientesCount: number;
  alumnosPendientes: string;
};

export async function fetchOneMinutePaperReservaState(
  reservaId: number,
): Promise<OneMinutePaperReservaState> {
  const { data } = await api.get<OneMinutePaperReservaState>(
    `/one-minute-paper/reservas/${reservaId}`,
  );
  return data;
}

export async function createOneMinutePaperEntry(input: {
  reservaId: number;
  alumnoId: number;
  nota: number;
  files: File[];
}) {
  const fd = new FormData();
  fd.append('nota', String(input.nota));
  input.files.forEach((f) => fd.append('files', f));

  const { data } = await api.post(
    `/one-minute-paper/reservas/${input.reservaId}/alumnos/${input.alumnoId}`,
    fd,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );

  return data;
}

export async function fetchOneMinutePaperReport(params: {
  alumnoId: number;
  desde: string;
  hasta: string;
  profesorId?: number;
}): Promise<OneMinutePaperReportResponse> {
  const { data } = await api.get<OneMinutePaperReportResponse>('/one-minute-paper/report', {
    params,
  });
  return data;
}

export async function fetchOneMinutePaperCompliance(params: {
  desde?: string;
  hasta?: string;
  search?: string;
  profesorId?: number;
}): Promise<OneMinutePaperComplianceResponse> {
  const { data } = await api.get<OneMinutePaperComplianceResponse>(
    '/one-minute-paper/compliance/profesores',
    { params },
  );
  return data;
}

export async function fetchOneMinutePaperComplianceReservas(
  profesorId: number,
  params: { desde?: string; hasta?: string },
): Promise<OneMinutePaperComplianceReservaRow[]> {
  const { data } = await api.get<OneMinutePaperComplianceReservaRow[]>(
    `/one-minute-paper/compliance/profesores/${profesorId}/reservas`,
    { params },
  );
  return data;
}

export async function fetchOneMinutePaperEvidenceBlob(
  entryId: number,
  index: number,
): Promise<Blob> {
  const { data } = await api.get(`/one-minute-paper/entries/${entryId}/evidencias/${index}/blob`, {
    responseType: 'blob',
  });
  return data as Blob;
}
